import { isNil } from '@gfe/core/utils/shared-utils';
import { parseISO8601Duration } from '@gfe/core/utils/time-utils';
import { TimeValue } from '@gfe/shared/components/dropdown/template/timepicker-dropdown/timepicker-dropdown.component';
import { processTimeValue } from '@gfe/shared/components/table/table-cell/utils';

import { Recurrence } from './recurrence';

export interface TimeWindow {
  start: string; // a naive time "10:00:00:
  duration: string; // a duration in ISO 8610
}

/**
 * Where the penalized (soft) region sits relative to the nominal window:
 * - `ExtendDeadline`: the tolerated-late region is added after the window finish.
 * - `HardDeadline`: the window finish stays a hard bound and the soft region sits
 *   inside the nominal window.
 */
export type LatenessAnchor = 'ExtendDeadline' | 'HardDeadline';

/** Time unit used to express the lateness penalty rate. */
export type LatenessTimeUnit = 'Second' | 'Minute' | 'Hour' | 'Day';

/** A graduated cost for arriving late within the tolerated-late region. */
export interface LatenessPenaltyCfg {
  penalty_per_duration_unit: number;
  duration_unit: LatenessTimeUnit;
}

/**
 * Optional configuration that makes a time window soft on lateness: arriving late
 * beyond the desired bound is tolerated up to a limit, at a graduated penalty
 * cost. When absent on a window, the window is hard on lateness.
 */
export interface LatenessCfg {
  accepted_lateness: string; // an ISO 8601 duration
  anchor?: LatenessAnchor; // defaults to 'ExtendDeadline' on the backend
  lateness_penalty: LatenessPenaltyCfg;
}

/**
 * Optional configuration allowing a window to be served earlier than its nominal
 * start at no cost; the window start simply moves back by the accepted earliness.
 */
export interface EarlinessCfg {
  accepted_earliness: string; // an ISO 8601 duration
}

export interface ArrivalCutoff {
  cutoff: string;
  lateness_cfg?: LatenessCfg;
}

export interface RecurringWindow {
  time_window: TimeWindow;
  recurrence: Recurrence;
  exceptions?: string[];
  earliness_cfg?: EarlinessCfg | null;
  lateness_cfg?: LatenessCfg | null;
  /**
   * Earliest date (inclusive, YYYY-MM-DD) on which the recurring window applies.
   * Null/absent means no lower bound.
   */
  valid_from?: string | null;
  /**
   * Latest date (inclusive, YYYY-MM-DD) on which the recurring window applies.
   * Null/absent means no upper bound.
   */
  valid_to?: string | null;
}

export interface DateTimeWindow {
  start: string;
  finish: string;
  earliness_cfg?: EarlinessCfg | null;
  lateness_cfg?: LatenessCfg | null;
  arrival_cutoff?: ArrivalCutoff | null;
}

export function makeStartAndFinishTimeValues(
  timeWindow: TimeWindow
): [TimeValue, TimeValue] | undefined {
  const windowStart = processTimeValue(timeWindow.start) as TimeValue | undefined;
  if (isNil(windowStart)) {
    return undefined;
  }

  const windowTotalMinutes = Math.round(
    parseISO8601Duration(timeWindow.duration).totalMilliseconds / 1000 / 60
  );
  const windowHours = Math.floor(windowTotalMinutes / 60);
  const windowMinutes = windowTotalMinutes - windowHours * 60;
  const extraHour = windowStart.minutes + windowMinutes >= 60 ? 1 : 0;

  const windowFinish: TimeValue = {
    hours: (windowStart.hours + windowHours + extraHour) % 24,
    minutes: (windowStart.minutes + windowMinutes) % 60
  };

  return [windowStart, windowFinish];
}
